import { Clock, Calculator, AlertTriangle, Save } from 'lucide-react'
import { useState, useEffect, useMemo } from 'react'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'

import {
  useTimeConfig,
  useUpdateTimeConfig,
  getStageEstimateDisplayName,
  type StageEstimateInput,
  type TimeConfigInput






} from '@/hooks'
import { cn } from '@/lib/utils'
import type {
  WorkflowStage, DevelopmentConfig
} from '@/hooks';

interface StageTimeEstimateFormProps {
  issueId: string
  enabledStages: WorkflowStage[]
  developmentConfig?: DevelopmentConfig
  onSaved?: () => void
}

/**
 * 阶段工时估算表单
 * Requirements: 2.1, 2.2, 2.3, 2.5
 */
export function StageTimeEstimateForm({
  issueId,
  enabledStages,
  developmentConfig = 'none',
  onSaved,
}: StageTimeEstimateFormProps) {
  const { data: timeConfig, isLoading } = useTimeConfig(issueId)
  const updateMutation = useUpdateTimeConfig()

  // 本地状态管理估算值
  const [estimates, setEstimates] = useState<Record<WorkflowStage, number>>({} as Record<WorkflowStage, number>)
  const [finalDeadline, setFinalDeadline] = useState<string>('')
  const [alertThreshold, setAlertThreshold] = useState<number>(80)
  const [isDirty, setIsDirty] = useState(false)

  // 可配置工时的阶段（排除 created 和 done）
  const configurableStages = useMemo(() => {
    return enabledStages.filter(
      (stage) => stage !== 'created' && stage !== 'done'
    )
  }, [enabledStages])

  // 从服务器数据初始化本地状态
  useEffect(() => {
    if (timeConfig) {
      const estimateMap: Record<WorkflowStage, number> = {} as Record<WorkflowStage, number>
      timeConfig.stageEstimates.forEach((e) => {
        estimateMap[e.stage] = e.estimatedHours
      })
      setEstimates(estimateMap)
      setFinalDeadline(timeConfig.finalDeadline ?? '')
      setAlertThreshold(timeConfig.alertThresholdPercent)
      setIsDirty(false)
    } else {
      // 初始化默认值
      const defaultEstimates: Record<WorkflowStage, number> = {} as Record<WorkflowStage, number>
      configurableStages.forEach((stage) => {
        defaultEstimates[stage] = 0
      })
      setEstimates(defaultEstimates)
      setFinalDeadline('')
      setAlertThreshold(80)
    }
  }, [timeConfig, configurableStages])

  // 计算总工时
  // Property 3: 总工时计算正确性 - 并行阶段取最大值
  const totalEstimatedHours = useMemo(() => {
    let total = 0
    let frontendHours = 0
    let backendHours = 0

    configurableStages.forEach((stage) => {
      const hours = estimates[stage] || 0
      if (stage === 'frontend_development') {
        frontendHours = hours
      } else if (stage === 'backend_development') {
        backendHours = hours
      } else {
        total += hours
      }
    })

    // 对于并行开发，取最大值
    if (developmentConfig === 'both') {
      total += Math.max(frontendHours, backendHours)
    } else {
      total += frontendHours + backendHours
    }

    return total
  }, [estimates, configurableStages, developmentConfig])

  const handleEstimateChange = (stage: WorkflowStage, value: string) => {
    const hours = parseFloat(value) || 0
    setEstimates((prev) => ({ ...prev, [stage]: hours }))
    setIsDirty(true)
  }

  const handleSave = async () => {
    const stageEstimates: StageEstimateInput[] = configurableStages.map((stage) => ({
      stage,
      estimatedHours: estimates[stage] || 0,
    }))

    const input: TimeConfigInput = {
      finalDeadline: finalDeadline || null,
      alertThresholdPercent: alertThreshold,
      stageEstimates,
    }

    await updateMutation.mutateAsync({ issueId, data: input })
    setIsDirty(false)
    onSaved?.()
  }

  if (isLoading) {
    return (
      <div className="rounded-lg border bg-card p-4">
        <div className="flex items-center gap-2 text-muted-foreground">
          <Clock className="h-4 w-4 animate-spin" />
          加载工时配置...
        </div>
      </div>
    )
  }

  return (
    <div className="rounded-lg border bg-card">
      {/* Header */}
      <div className="flex items-center justify-between border-b px-4 py-3">
        <div className="flex items-center gap-2">
          <Clock className="h-4 w-4 text-muted-foreground" />
          <h4 className="text-sm font-medium">工时估算</h4>
        </div>
        {isDirty && (
          <span className="text-xs text-yellow-600 dark:text-yellow-400">
            有未保存的更改
          </span>
        )}
      </div>

      {/* Content */}
      <div className="p-4 space-y-4">
        {/* 阶段工时输入 */}
        <div className="space-y-3">
          <Label className="text-sm font-medium">各阶段预估工时（小时）</Label>
          <div className="grid gap-3 sm:grid-cols-2">
            {configurableStages.map((stage) => (
              <div key={stage} className="space-y-1.5">
                <Label htmlFor={`estimate-${stage}`} className="text-xs text-muted-foreground">
                  {getStageEstimateDisplayName(stage)}
                  {developmentConfig === 'both' &&
                    (stage === 'frontend_development' || stage === 'backend_development') && (
                      <span className="ml-1 text-blue-500">(并行)</span>
                    )}
                </Label>
                <Input
                  id={`estimate-${stage}`}
                  type="number"
                  min="0"
                  step="0.5"
                  value={estimates[stage] || ''}
                  onChange={(e) => handleEstimateChange(stage, e.target.value)}
                  placeholder="0"
                  className="h-8"
                />
              </div>
            ))}
          </div>
        </div>

        {/* 总工时显示 */}
        <div className="flex items-center justify-between rounded-md bg-muted/50 px-3 py-2">
          <div className="flex items-center gap-2">
            <Calculator className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm">总预估工时</span>
          </div>
          <span className="text-lg font-semibold">
            {totalEstimatedHours.toFixed(1)} 小时
          </span>
        </div>

        {/* 并行开发提示 */}
        {developmentConfig === 'both' && (
          <div className="flex items-start gap-2 rounded-md bg-blue-50 p-3 text-sm text-blue-700 dark:bg-blue-900/20 dark:text-blue-400">
            <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
            <p>
              前后端并行开发时，总工时按两者中较长的计算。
              当前：前端 {estimates.frontend_development || 0}h，
              后端 {estimates.backend_development || 0}h，
              取 {Math.max(estimates.frontend_development || 0, estimates.backend_development || 0)}h
            </p>
          </div>
        )}

        {/* 最终截止日期 */}
        <div className="space-y-1.5">
          <Label htmlFor="final-deadline" className="text-sm font-medium">
            最终截止日期（可选）
          </Label>
          <Input
            id="final-deadline"
            type="datetime-local"
            value={finalDeadline}
            onChange={(e) => {
              setFinalDeadline(e.target.value)
              setIsDirty(true)
            }}
            className="h-9"
          />
        </div>

        {/* 预警阈值 */}
        <div className="space-y-1.5">
          <Label htmlFor="alert-threshold" className="text-sm font-medium">
            预警阈值（%）
          </Label>
          <div className="flex items-center gap-2">
            <Input
              id="alert-threshold"
              type="number"
              min="0"
              max="100"
              value={alertThreshold}
              onChange={(e) => {
                setAlertThreshold(parseInt(e.target.value) || 80)
                setIsDirty(true)
              }}
              className="h-9 w-24"
            />
            <span className="text-sm text-muted-foreground">
              超过此比例时显示预警
            </span>
          </div>
        </div>

        {/* 保存按钮 */}
        <div className="flex justify-end pt-2">
          <Button
            onClick={() => void handleSave()}
            disabled={updateMutation.isPending || !isDirty}
            size="sm"
          >
            <Save className="mr-2 h-4 w-4" />
            {updateMutation.isPending ? '保存中...' : '保存工时配置'}
          </Button>
        </div>

        {/* 错误提示 */}
        {updateMutation.error && (
          <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
            {updateMutation.error.message}
          </div>
        )}
      </div>
    </div>
  )
}

/**
 * 紧凑版工时估算显示（只读）
 * Requirements: 2.5
 */
export function StageTimeEstimateSummary({
  issueId,
  className,
}: {
  issueId: string
  className?: string
}) {
  const { data: timeConfig, isLoading } = useTimeConfig(issueId)

  if (isLoading) {
    return (
      <div className={cn('text-sm text-muted-foreground', className)}>
        加载中...
      </div>
    )
  }

  if (!timeConfig || timeConfig.stageEstimates.length === 0) {
    return (
      <div className={cn('text-sm text-muted-foreground', className)}>
        未配置工时估算
      </div>
    )
  }

  return (
    <div className={cn('space-y-2', className)}>
      <div className="flex items-center gap-2 text-sm">
        <Clock className="h-4 w-4 text-muted-foreground" />
        <span>总预估: {timeConfig.totalEstimatedHours.toFixed(1)}h</span>
      </div>
      {timeConfig.finalDeadline && (
        <div className="text-xs text-muted-foreground">
          截止: {new Date(timeConfig.finalDeadline).toLocaleString()}
        </div>
      )}
    </div>
  )
}
